import { Form, Input, Radio, Tooltip } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerImageInfo } from '@/api/type-server'
import { Button, message, Trans } from '@/components'
import { Modal } from '@/components/base/Modal'
import {
  getUtf8ByteLength,
  IMAGE_DESCRIPTION_MAX_BYTES,
  IMAGE_NAME_MAX_LENGTH,
  ImageFormat,
  ImageProvider,
  isImportFormReady,
  isValidRegistryUrl,
  REGISTRY_URL_MAX_LENGTH,
} from '@/consts/image'

type ImageType = 'container' | 'vm'
type RegistryType = 'public' | 'private'

interface UploadCustomizeImageModalProps {
  open: boolean
  onClose: () => void
  imageList: ServerImageInfo[]
  onSuccess?: () => void
}

interface FormValues {
  imageType: ImageType
  registryType: RegistryType
  imageName: string
  registryLink: string
  repositoryUsername?: string
  repositoryPassword?: string
  description?: string
}

const initialValues: FormValues = {
  imageType: 'container',
  registryType: 'public',
  imageName: '',
  registryLink: '',
}

export function UploadCustomizeImageModal({
  open,
  onClose,
  imageList,
  onSuccess,
}: UploadCustomizeImageModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const [submitting, setSubmitting] = useState(false)

  const imageName = Form.useWatch('imageName', form)
  const registryLink = Form.useWatch('registryLink', form)
  const registryType = Form.useWatch('registryType', form) as RegistryType | undefined
  const repositoryUsername = Form.useWatch('repositoryUsername', form)
  const repositoryPassword = Form.useWatch('repositoryPassword', form)

  const canSubmit = useMemo(
    () =>
      isImportFormReady({
        imageName,
        registryLink,
        registryType,
        repositoryUsername,
        repositoryPassword,
      }),
    [imageName, registryLink, registryType, repositoryUsername, repositoryPassword],
  )

  const closeAndReset = () => {
    form.resetFields()
    onClose()
  }

  const checkDuplicateName = (targetName: string): boolean => {
    const lowerTargetName = targetName.toLowerCase()
    return imageList.some((image) => {
      const lowerAlias = (image.alias || '').toLowerCase()
      const lowerName = (image.name || '').toLowerCase()
      return lowerAlias === lowerTargetName || lowerName === lowerTargetName
    })
  }

  const onSubmit = async (values: FormValues) => {
    const imageName = values.imageName.trim().toLowerCase()

    if (checkDuplicateName(imageName)) {
      message.error(t('customImage.importModal.error.duplicateName'))
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        url: values.registryLink.trim(),
        name: imageName,
        description: values.description?.trim() || '',
        size: 0,
        checksum: '',
        // VM URL import is not yet supported (radio is disabled); defensive fallback to Container.
        format: values.imageType === 'vm' ? ImageFormat.VM : ImageFormat.Container,
        image_provider: ImageProvider.RegistryURL,
        repo_username:
          values.registryType === 'private' ? values.repositoryUsername?.trim() || '' : '',
        repo_passwd:
          values.registryType === 'private' ? values.repositoryPassword?.trim() || '' : '',
      }

      const res = await API.registerImage(payload)
      if (res.code !== 0) {
        return
      }

      message.success(t('customImage.importModal.success'))
      onSuccess?.()
      closeAndReset()
    } catch (error) {
      console.error('Failed to import image:', error)
      message.error(t('customImage.importModal.error.importFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={closeAndReset} className="w-[640px]">
      <Modal.Header>
        <Trans i18nKey="customImage.importModal.title">Import from URL</Trans>
      </Modal.Header>
      <Modal.Body>
        <Form
          form={form}
          layout="vertical"
          name="import-custom-image-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          className="[&>.ant-form-item]:mb-[16px]"
        >
          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.importModal.imageType">Image Type</Trans>
              </div>
            }
            name="imageType"
            rules={[{ required: true }]}
          >
            <Radio.Group className="w-full grid grid-cols-2 gap-3">
              <Radio
                value="container"
                className="flex items-center px-4 py-3 border border-gray100 rounded-lg t-sb text-primary hover:border-secondary [&.ant-radio-wrapper-checked]:border-secondary [&.ant-radio-wrapper-checked]:bg-highlight/10"
              >
                {t('customImage.filter.container')}
              </Radio>
              <Tooltip title={t('customImage.importModal.vmDisabledTooltip')} mouseEnterDelay={0}>
                {/* span wrapper lets Tooltip catch hover events on a disabled Radio */}
                <span className="flex">
                  <Radio
                    value="vm"
                    disabled
                    className="flex-1 flex items-center px-4 py-3 border border-gray100 rounded-lg t-sb text-primary hover:border-secondary [&.ant-radio-wrapper-checked]:border-secondary [&.ant-radio-wrapper-checked]:bg-highlight/10"
                  >
                    {t('customImage.filter.virtualMachine')}
                  </Radio>
                </span>
              </Tooltip>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.importModal.imageName">Image Name</Trans>
              </div>
            }
            name="imageName"
            rules={[
              {
                required: true,
                message: t('customImage.importModal.validation.enterImageName'),
              },
              {
                max: IMAGE_NAME_MAX_LENGTH,
                message: t('customImage.importModal.validation.imageNameTooLong', {
                  count: IMAGE_NAME_MAX_LENGTH,
                }),
              },
            ]}
          >
            <Input
              maxLength={IMAGE_NAME_MAX_LENGTH}
              showCount
              className="h-11 px-4 py-3 t-sb placeholder:text-tertiary placeholder:t-sm"
              placeholder={t('customImage.importModal.imageNamePlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.importModal.registryLink">Registry Link</Trans>
              </div>
            }
            name="registryLink"
            rules={[
              {
                required: true,
                message: t('customImage.importModal.validation.enterRegistryLink'),
              },
              {
                max: REGISTRY_URL_MAX_LENGTH,
                message: t('customImage.importModal.validation.registryLinkTooLong', {
                  count: REGISTRY_URL_MAX_LENGTH,
                }),
              },
              {
                validator: (_, value: string | undefined) => {
                  if (!value?.trim()) return Promise.resolve()
                  if (isValidRegistryUrl(value)) return Promise.resolve()
                  return Promise.reject(
                    new Error(t('customImage.importModal.validation.invalidRegistryLink')),
                  )
                },
              },
            ]}
          >
            <Input
              maxLength={REGISTRY_URL_MAX_LENGTH}
              className="h-11 px-4 py-3 t-sb placeholder:text-tertiary placeholder:t-sm"
              placeholder={t('customImage.importModal.registryLinkPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.importModal.registryType">Registry Type</Trans>
              </div>
            }
            name="registryType"
            rules={[{ required: true }]}
          >
            <Radio.Group className="w-full grid grid-cols-2 gap-3">
              {[
                { value: 'public', label: t('customImage.importModal.public') },
                { value: 'private', label: t('customImage.importModal.private') },
              ].map((item) => (
                <Radio
                  key={item.value}
                  value={item.value}
                  className="flex-1 flex items-center px-4 py-3 border border-gray100 rounded-lg t-sb text-primary hover:border-secondary [&.ant-radio-wrapper-checked]:border-secondary [&.ant-radio-wrapper-checked]:bg-highlight/10"
                >
                  {item.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          {registryType === 'private' && (
            <>
              <Form.Item
                label={
                  <div className="t-sb block text-primary">
                    <Trans i18nKey="customImage.importModal.repositoryUsername">
                      Repository Username
                    </Trans>
                  </div>
                }
                name="repositoryUsername"
                rules={[
                  {
                    required: true,
                    message: t('customImage.importModal.validation.enterRepositoryUsername'),
                  },
                ]}
              >
                <Input
                  className="h-11 px-4 py-3 t-sb placeholder:text-tertiary placeholder:t-sm"
                  placeholder={t('customImage.importModal.repositoryUsernamePlaceholder')}
                />
              </Form.Item>
              <Form.Item
                label={
                  <div className="t-sb block text-primary">
                    <Trans i18nKey="customImage.importModal.repositoryPassword">
                      Repository Password
                    </Trans>
                  </div>
                }
                name="repositoryPassword"
                rules={[
                  {
                    required: true,
                    message: t('customImage.importModal.validation.enterRepositoryPassword'),
                  },
                ]}
              >
                <Input.Password
                  className="h-11 px-4 py-3 t-sb placeholder:text-tertiary placeholder:t-sm"
                  placeholder={t('customImage.importModal.repositoryPasswordPlaceholder')}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            label={
              <div className="t-sb block text-primary">
                <Trans i18nKey="customImage.importModal.description">Description</Trans>
              </div>
            }
            name="description"
            rules={[
              {
                validator: (_, value: string | undefined) => {
                  if (!value) return Promise.resolve()
                  const bytes = getUtf8ByteLength(value)
                  if (bytes <= IMAGE_DESCRIPTION_MAX_BYTES) return Promise.resolve()
                  return Promise.reject(
                    new Error(
                      t('customImage.importModal.validation.descriptionTooLong', {
                        current: bytes,
                        max: IMAGE_DESCRIPTION_MAX_BYTES,
                      }),
                    ),
                  )
                },
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              className="px-4 py-3 t-sb placeholder:text-tertiary placeholder:t-sm"
              placeholder={t('customImage.importModal.descriptionPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={closeAndReset} disabled={submitting}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" onClick={() => form.submit()} disabled={submitting || !canSubmit}>
          <Trans i18nKey="customImage.importModal.uploadBtn">Upload</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
